import Backdrop from "@components/Backdrop"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import useElevarEvents, { ElevarProductProps } from "@hooks/useElevarEvents"
import useEnhancedEcommerceEvents, {
  GTMProductProps,
} from "@hooks/useEnhancedEcommerceEvents"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import { formatContentfulVariant } from "@utils/index"
import _ from "lodash-es"
import React from "react"
import styled from "styled-components"

import Form from "./Form"
import LanguageSelector from "./LanguageSelector"
import SizeSelector from "./SizeSelector"

const openProps = `
  opacity: 1;
  transform: translateX(0);
  transition: opacity ${animation.quick} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
`
const Container = styled.div<{ $isOpen?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  opacity: 0;
  transform: translateX(100%);
  transition:
    opacity ${animation.medium} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
  will-change: opacity, transform;
  background-color: ${colors.offWhite};
  cursor: default;
  ${({ $isOpen }) => ($isOpen ? openProps : "")}

  @media ${breakpoints.tablet} {
    max-width: 64%;
  }

  @media ${breakpoints.laptop} {
    max-width: calc(5 / 12 * 100vw);
  }
`

interface VariantSelectorProps {
  addToCart: any
  availableVariants: any
  isOpen: boolean
  onRequestClose: () => void
  product: any
  showPromoToast?: boolean
}

const VariantSelector: React.FC<VariantSelectorProps> = ({
  addToCart,
  availableVariants,
  isOpen,
  onRequestClose,
  product,
  showPromoToast,
}) => {
  const menuRef: any = React.useRef(null)

  useModalBackgroundClose({
    isOpen,
    modalRef: menuRef,
    onRequestClose: onRequestClose,
  })

  const [languageSelectorIsOpen, setLanguageSelectorIsOpen] =
    React.useState(false)
  const [languageSelectorData, setLanguageSelectorData] = React.useState([])

  const openLanguageSelector = (productLanguages) => {
    setLanguageSelectorData(productLanguages)
    setLanguageSelectorIsOpen(true)
  }

  const closeLanguageSelector = () => {
    setLanguageSelectorIsOpen(false)
  }

  const [sizeSelectorIsOpen, setSizeSelectorIsOpen] = React.useState(false)
  const [sizeSelectorData, setSizeSelectorData] = React.useState([])

  const openSizeSelector = (productSizes) => {
    setSizeSelectorData(productSizes)
    setSizeSelectorIsOpen(true)
  }

  const closeSizeSelector = () => {
    setSizeSelectorIsOpen(false)
  }

  const { onAddProductToCart } = useEnhancedEcommerceEvents()
  const { elevarOnAddToCart } = useElevarEvents()

  const formatProduct = (p) => {
    const formattedVariants = p.variants.map(formatContentfulVariant)
    const variantsWithClickHandlers = formattedVariants.map((v) => {
      const onAddToCart = (quantity) => {
        let multipleDevices
        const productDevices = p.devices || []

        if (productDevices.length > 0) {
          multipleDevices = productDevices.reduce((acc, curr) => {
            if (acc) {
              acc = acc + `, ${curr.name}`
            } else {
              acc = curr.name
            }

            return acc
          }, undefined)
        }

        const imageSrc = _.get(v, "images[0]", "")
        const [cleanedSrc] = imageSrc.split("?")

        addToCart({
          cartProduct: {
            brand: p.brand?.name,
            builtInMagnet: !!p.builtInMagnet,
            color: v.color,
            compareAtPrice: v.compareAtPrice,
            contentfulProductId: p.contentful_id,
            contentfulVariantId: v.contentfulVariantId,
            device: multipleDevices ? multipleDevices : p.device?.name,
            deviceType: p.device?.type?.name,
            image: `https:${cleanedSrc}`,
            language: p.language,
            marketPrices: v.marketPrices,
            price: v.price,
            productName: p.productName,
            productSlug: p.slug,
            productSubType: p.productSubType?.slug,
            quantity,
            shopifyProductId: p.shopifyProduct?.shopifyProductId,
            shopifyVariantId: v.variantId,
            size: v.size,
            sku: v.sku,
            variantName: v.name,
          },
          showPromoToast: showPromoToast || false,
        })

        const elevarProduct: ElevarProductProps = {
          brand: "Urban Armor Gear",
          device: multipleDevices ? multipleDevices : p.device?.name,
          deviceType: p.device?.type?.name,
          image: `https:${cleanedSrc}`,
          list: "upsell",
          price: v.price,
          productId: p.shopifyProduct?.shopifyProductId,
          productName: p.productName,
          quantity: String(quantity),
          sku: v.sku,
          variantId: v.variantId,
          variantName: v.name,
        }

        elevarOnAddToCart(elevarProduct, "upsell")

        const gtmProduct: GTMProductProps = {
          brand: p.brand.name,
          device: multipleDevices ? multipleDevices : p.device.name,
          deviceType: p.device?.type?.name,
          price: v.price,
          productId: p.shopifyProduct?.shopifyProductId,
          productName: p.productName,
          quantity,
          sku: v.sku,
          variantName: v.name,
        }

        onAddProductToCart(gtmProduct)
        onRequestClose()
      }

      return {
        ...v,
        onAddToCart,
      }
    })

    return {
      ...p,
      variants: variantsWithClickHandlers,
    }
  }

  const formattedProduct = React.useMemo(() => {
    return formatProduct(product)
  }, [product])

  return (
    <Backdrop $isOpen={isOpen}>
      <Container $isOpen={isOpen} ref={menuRef}>
        <Form
          availableVariants={availableVariants}
          onClickLanguageSelector={openLanguageSelector}
          onClickSizeSelector={openSizeSelector}
          onRequestClose={onRequestClose}
          onRequestCloseLanguageSelector={closeLanguageSelector}
          onRequestCloseSizeSelector={closeSizeSelector}
          productName={formattedProduct.productName}
          variants={formattedProduct.variants}
        />
        <LanguageSelector
          isOpen={languageSelectorIsOpen}
          languages={languageSelectorData}
          onRequestClose={closeLanguageSelector}
        />
        <SizeSelector
          isOpen={sizeSelectorIsOpen}
          onRequestClose={closeSizeSelector}
          sizes={sizeSelectorData}
        />
      </Container>
    </Backdrop>
  )
}

export default VariantSelector
